import {
  Box as BoxBase,
  Button as ButtonBase,
  Flex as FlexBase,
  keyframes,
  Tooltip as TooltipBase,
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';
import { NATUREZA, ROTULO_DA_CLASSE, textoDaVariacao, Tom } from './formato';
import { Alerta, Classe } from './tipos';

// Cast Chakra components to bypass TS2590 ("union type too complex").
export const Box = BoxBase as React.ComponentType<any>;
export const Button = ButtonBase as React.ComponentType<any>;
export const Flex = FlexBase as React.ComponentType<any>;
const Tooltip = TooltipBase as React.ComponentType<any>;

// As peças comuns às três abas do painel. As cores são as variáveis --pa-*
// (visual.ts), que passam o contraste nos dois temas.

export function Cartao({ children, ...props }: any) {
  return (
    <Box
      bg="var(--pa-superficie)"
      border="1px solid var(--pa-linha)"
      borderRadius="12px"
      boxShadow="var(--pa-sombra)"
      minW={0}
      {...props}
    >
      {children}
    </Box>
  );
}

// O ícone ao lado de um título: o que o número mostra e como ler.
export function Explicacao({ texto }: { texto: string }) {
  return (
    <Tooltip label={texto} hasArrow placement="top" maxW="320px" openDelay={150}>
      <Box
        as="button"
        type="button"
        aria-label={`O que isto mostra: ${texto}`}
        display="inline-grid"
        placeItems="center"
        w="22px"
        h="22px"
        p={0}
        flexShrink={0}
        border="none"
        borderRadius="999px"
        bg="transparent"
        color="var(--pa-tinta-3)"
        cursor="help"
        _hover={{ color: 'var(--pa-tinta)' }}
        _focusVisible={{ outline: '2px solid var(--pa-dado)', outlineOffset: '1px' }}
      >
        <FiInfo size={15} aria-hidden="true" />
      </Box>
    </Tooltip>
  );
}

// O período da visão, numa linha só, logo abaixo das abas: quem troca de aba vê
// na hora de quando são os números. O que a visão mostra fica nas dicas (ⓘ).
export function LinhaDoPeriodo({ children }: { children: React.ReactNode }) {
  return (
    <Box fontSize="13px" lineHeight="20px" color="var(--pa-tinta-2)">
      {children}
    </Box>
  );
}

// Título de uma seção: o nome, a legenda (o período, o que mostra) e as ações.
export function Secao({ titulo, legenda, explicacao, acoes, nivel = 'h3', children, ...props }: any) {
  return (
    <Box as="section" minW={0} {...props}>
      <Flex justify="space-between" align="flex-end" wrap="wrap" sx={{ gap: '8px 16px' }} mb="12px">
        <Box minW={0}>
          <Flex align="center" sx={{ gap: '4px' }}>
            <Box as={nivel} m={0} fontSize="16px" fontWeight={700} lineHeight="22px">
              {titulo}
            </Box>
            {explicacao && <Explicacao texto={explicacao} />}
          </Flex>
          {legenda && (
            <Box fontSize="13px" color="var(--pa-tinta-3)" mt="2px">
              {legenda}
            </Box>
          )}
        </Box>
        {acoes}
      </Flex>
      {children}
    </Box>
  );
}

export function Botao({ children, ...props }: any) {
  return (
    <Button
      size="sm"
      h="34px"
      px="12px"
      borderRadius="8px"
      fontSize="13px"
      fontWeight={600}
      bg="var(--pa-superficie)"
      color="var(--pa-tinta)"
      border="1px solid var(--pa-linha)"
      _hover={{ bg: 'var(--pa-superficie-2)' }}
      _active={{ bg: 'var(--pa-superficie-2)' }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function BotaoDeTexto({ children, ...props }: any) {
  return (
    <Button
      size="sm"
      variant="ghost"
      h="32px"
      px="8px"
      fontSize="13px"
      fontWeight={600}
      color="var(--pa-marca-tinta)"
      _hover={{ bg: 'var(--pa-marca-suave)' }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function Selo({ classe, quantidade }: { classe: Classe; quantidade?: number }) {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      sx={{ gap: '6px' }}
      px="8px"
      py="2px"
      borderRadius="999px"
      fontSize="12px"
      fontWeight={600}
      lineHeight="16px"
      whiteSpace="nowrap"
      color={`var(--pa-${classe})`}
      bg={`var(--pa-${classe}-fundo)`}
    >
      <Box as="i" display="block" w="7px" h="7px" borderRadius="50%" bg="currentColor" />
      {ROTULO_DA_CLASSE[classe]}
      {quantidade != null && ` · ${quantidade}`}
    </Box>
  );
}

export function Etiqueta({ children, tom }: { children: React.ReactNode; tom?: Tom | 'neutro' }) {
  const cores: Record<string, [string, string]> = {
    novo: ['var(--pa-dado)', 'var(--pa-superficie-2)'],
    reaberto: ['var(--pa-atencao)', 'var(--pa-atencao-fundo)'],
    persistente: ['var(--pa-tinta-2)', 'var(--pa-superficie-2)'],
    neutro: ['var(--pa-tinta-2)', 'transparent'],
  };
  const [cor, fundo] = cores[tom || 'neutro'];
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      px="7px"
      py="1px"
      borderRadius="999px"
      border="1px solid var(--pa-linha)"
      fontSize="12px"
      fontWeight={500}
      lineHeight="17px"
      whiteSpace="nowrap"
      color={cor}
      bg={fundo}
    >
      {children}
    </Box>
  );
}

export function NaturezaDoAlerta({ natureza }: { natureza: Alerta['natureza'] }) {
  return <Etiqueta>{NATUREZA[natureza]}</Etiqueta>;
}

// A variação como veio do servidor. Sem base (anterior zero), diz isso em vez
// de um número. `inverter`: subir é ruim (não usado hoje, mas a cor segue o sinal).
export function Variacao({ valor, sufixo = '%' }: { valor: number | null | undefined; sufixo?: string }) {
  if (valor == null)
    return (
      <Box as="span" fontSize="12px" color="var(--pa-tinta-3)">
        {textoDaVariacao(valor)}
      </Box>
    );
  const sobe = valor > 0;
  const igual = valor === 0;
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      px="6px"
      py="1px"
      borderRadius="6px"
      fontSize="12px"
      fontWeight={600}
      lineHeight="17px"
      whiteSpace="nowrap"
      color={igual ? 'var(--pa-tinta-2)' : sobe ? 'var(--pa-sobe)' : 'var(--pa-desce)'}
      bg={igual ? 'var(--pa-superficie-2)' : sobe ? 'var(--pa-sobe-fundo)' : 'var(--pa-desce-fundo)'}
    >
      {igual ? '=' : sobe ? '↑' : '↓'} {textoDaVariacao(valor, sufixo).replace(/^[+−]/, '')}
    </Box>
  );
}

// Um indicador: rótulo, número e a linha de baixo (variação ou legenda).
export function Kpi({
  rotulo,
  valor,
  titulo,
  delta,
  sufixo,
  legenda,
  explicacao,
  destaque,
  children,
  ...props
}: {
  rotulo: string;
  valor: string;
  titulo?: string;
  delta?: number | null;
  sufixo?: string;
  legenda?: React.ReactNode;
  explicacao?: string;
  destaque?: boolean;
  children?: React.ReactNode;
  [k: string]: any;
}) {
  return (
    <Cartao px="18px" py="16px" display="flex" flexDirection="column" sx={{ gap: '6px' }} {...props}>
      {/* O nome à esquerda e a variação no canto: o selo se lê antes do número. */}
      {/* Sem espaço (celular), o selo desce para baixo do nome em vez de cortá-lo. */}
      <Flex align="center" justify="space-between" wrap="wrap" sx={{ gap: '4px 8px' }} minH="22px">
        <Flex align="center" sx={{ gap: '2px' }} minW={0} fontSize="13px" fontWeight={500} color="var(--pa-tinta-2)">
          <Box as="span">{rotulo}</Box>
          {explicacao && <Explicacao texto={explicacao} />}
        </Flex>
        {delta !== undefined && <Variacao valor={delta} sufixo={sufixo} />}
      </Flex>
      <Box
        fontSize={destaque ? { base: '26px', xl: '30px' } : { base: '22px', xl: '26px' }}
        lineHeight={destaque ? { base: '34px', xl: '38px' } : { base: '30px', xl: '34px' }}
        fontWeight={700}
        letterSpacing="-0.02em"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        title={titulo}
      >
        {valor}
      </Box>
      {legenda && (
        <Box fontSize="12px" lineHeight="17px" color="var(--pa-tinta-3)">
          {legenda}
        </Box>
      )}
      {children}
    </Cartao>
  );
}

// Um cartão com o título dentro, em cima: o nome, a legenda e as ações à direita.
export function CartaoComTitulo({ titulo, legenda, explicacao, acoes, children, corpo, ...props }: any) {
  return (
    <Cartao as="section" aria-label={titulo} display="flex" flexDirection="column" {...props}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" sx={{ gap: '6px 16px' }} px="16px" pt="14px" pb="10px">
        <Box minW={0}>
          <Flex as="h3" m={0} align="center" sx={{ gap: '2px' }} fontSize="16px" lineHeight="22px" fontWeight={700}>
            {titulo}
            {explicacao && <Explicacao texto={explicacao} />}
          </Flex>
          {legenda && (
            <Box fontSize="12px" lineHeight="17px" color="var(--pa-tinta-3)" mt="2px">
              {legenda}
            </Box>
          )}
        </Box>
        {acoes}
      </Flex>
      <Box px="16px" pb="14px" minW={0} {...corpo}>
        {children}
      </Box>
    </Cartao>
  );
}

// Aviso de uma linha: sem histórico, poucos pedidos, margem parcial.
export function Aviso({ children, tom = 'info' }: { children: React.ReactNode; tom?: 'info' | 'atencao' }) {
  return (
    <Flex
      px="14px"
      py="10px"
      borderRadius="10px"
      align="flex-start"
      sx={{ gap: '10px' }}
      fontSize="13px"
      lineHeight="20px"
      color="var(--pa-tinta-2)"
      bg={tom === 'atencao' ? 'var(--pa-atencao-fundo)' : 'var(--pa-superficie-2)'}
      border="1px solid var(--pa-linha)"
    >
      <Box as="span" mt="2px" flexShrink={0} color={tom === 'atencao' ? 'var(--pa-atencao)' : undefined}>
        <FiInfo size={15} aria-hidden="true" />
      </Box>
      <Box minW={0}>{children}</Box>
    </Flex>
  );
}

export function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <Box
      px="16px"
      py="22px"
      textAlign="center"
      fontSize="13px"
      color="var(--pa-tinta-3)"
      border="1px dashed var(--pa-linha)"
      borderRadius="10px"
    >
      {children}
    </Box>
  );
}

const pulso = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

export function Esqueleto(props: any) {
  return (
    <Box
      bg="var(--pa-superficie)"
      border="1px solid var(--pa-linha)"
      borderRadius="12px"
      animation={`${pulso} 1.4s ease-in-out infinite`}
      {...props}
    />
  );
}

// A grade do painel: 12 colunas a partir de telas largas, uma no celular.
export function Grade({ children, ...props }: any) {
  return (
    <Box
      display="grid"
      gridTemplateColumns={{ base: 'minmax(0, 1fr)', lg: 'repeat(12, minmax(0, 1fr))' }}
      sx={{ gap: '20px' }}
      alignItems="start"
      {...props}
    >
      {children}
    </Box>
  );
}

// Faixa de indicadores: quantos couberem lado a lado, cada um com no mínimo 180px.
export function Faixa({ children, minimo = '180px', ...props }: any) {
  return (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(auto-fit, minmax(min(${minimo}, 100%), 1fr))`}
      sx={{ gap: '12px' }}
      {...props}
    >
      {children}
    </Box>
  );
}
